import type { ReactNode } from "react"

import type { EventCreateInput } from "@/lib/guild-events-api"

import { DateTimeField } from "@/components/custom/date-time-field"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import { getRoleLabel } from "@/core/guild-events/labels"
import {
  WWM_EVENT_ROLE_CATALOG,
  type WwmEventRoleKey,
} from "@/core/guild-events/types"
import { formatDateTimeOffsetHint } from "@/lib/guild-date-format"

type EventFormCommon = {
  title: string
  description: string
  startAt: string
  rsvpEnabled: boolean
}
const DEFAULT_RAID_ROLES: WwmEventRoleKey[] = ["tank", "dps", "healer"]

export type EventFormState =
  | (EventFormCommon & {
      eventKind: "raid"
      eventRoles: WwmEventRoleKey[]
      signupCloseAt: string
    })
  | (EventFormCommon & {
      eventKind: "attendance"
      eventRoles: []
      signupCloseAt: null
    })

export type EventFormInput = Pick<
  EventCreateInput,
  "title" | "description" | "startAt" | "rsvpEnabled"
> &
  (
    | {
        eventKind: "raid"
        eventRoles: [WwmEventRoleKey, ...WwmEventRoleKey[]]
        signupCloseAt: string
      }
    | {
        eventKind: "attendance"
        eventRoles: []
        signupCloseAt: null
      }
  )

type EventFormErrors = Partial<
  Record<"title" | "startAt" | "eventRoles" | "signupCloseAt", string>
>

const firstError = (errors: unknown[]) => {
  const value = errors[0]
  if (typeof value === "string") return value
  if (value instanceof Error) return value.message
  return null
}

function FieldError({ error, id }: { error: string | null; id: string }) {
  return error ? (
    <p id={id} className="text-sm text-destructive">
      {error}
    </p>
  ) : null
}

type EventFormField<T> = (
  children: (field: {
    state: { value: T; meta: { errors: unknown[] } }
    handleBlur: () => void
    handleChange: (value: T) => void
  }) => ReactNode,
) => ReactNode

export type EventFormBindings = {
  values: EventFormState
  eventKind: EventFormField<"raid" | "attendance">
  title: EventFormField<string>
  description: EventFormField<string>
  eventRoles: EventFormField<WwmEventRoleKey[]>
  startAt: EventFormField<string>
  signupCloseAt: EventFormField<string>
  rsvpEnabled: EventFormField<boolean>
  setEventRoles: (value: WwmEventRoleKey[]) => void
  setSignupCloseAt: (value: string | null) => void
}

export function EventFormFields({
  bindings,
  eventKindReadOnly = false,
}: {
  bindings: EventFormBindings
  eventKindReadOnly?: boolean
}) {
  return (
    <>
      {bindings.eventKind((field) => (
        <fieldset
          className="grid gap-2 md:col-span-2"
          disabled={eventKindReadOnly}
        >
          <legend className="mb-3 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            Event Kind
          </legend>
          <RadioGroup
            value={field.state.value}
            onValueChange={(eventKind) => {
              if (eventKind === "raid") {
                field.handleChange("raid")
                bindings.setEventRoles(
                  bindings.values.eventKind === "raid"
                    ? bindings.values.eventRoles
                    : DEFAULT_RAID_ROLES,
                )
                bindings.setSignupCloseAt(
                  bindings.values.eventKind === "raid"
                    ? bindings.values.signupCloseAt
                    : "",
                )
                return
              }
              field.handleChange("attendance")
              bindings.setEventRoles([])
              bindings.setSignupCloseAt(null)
            }}
            disabled={eventKindReadOnly}
            className="flex flex-wrap gap-4"
          >
            <div className="flex items-center gap-2 text-sm">
              <RadioGroupItem id="event-kind-raid" value="raid" />
              <Label htmlFor="event-kind-raid">Raid</Label>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <RadioGroupItem id="event-kind-attendance" value="attendance" />
              <Label htmlFor="event-kind-attendance">Attendance</Label>
            </div>
          </RadioGroup>
        </fieldset>
      ))}
      {bindings.title((field) => {
        const error = firstError(field.state.meta.errors)
        const errorId = "event-title-error"
        return (
          <div className="grid gap-2 md:col-span-2">
            <label
              htmlFor="event-title"
              className="text-xs font-semibold tracking-wide text-muted-foreground uppercase"
            >
              Title
            </label>
            <Input
              id="event-title"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(event) => field.handleChange(event.target.value)}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
              required
            />
            <FieldError error={error} id={errorId} />
          </div>
        )
      })}
      {bindings.description((field) => (
        <div className="grid gap-2 md:col-span-2">
          <label
            htmlFor="event-description"
            className="text-xs font-semibold tracking-wide text-muted-foreground uppercase"
          >
            Description (optional)
          </label>
          <Textarea
            id="event-description"
            value={field.state.value}
            onBlur={field.handleBlur}
            onChange={(event) => field.handleChange(event.target.value)}
            rows={4}
          />
        </div>
      ))}
      {bindings.values.eventKind === "raid"
        ? bindings.eventRoles((field) => {
            const error = firstError(field.state.meta.errors)
            const errorId = "event-roles-error"
            return (
              <fieldset
                className="grid gap-2 md:col-span-2"
                aria-describedby={error ? errorId : undefined}
                aria-invalid={error ? true : undefined}
              >
                <legend className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                  Event roles
                </legend>
                <div className="flex flex-wrap gap-4">
                  {(
                    Object.keys(WWM_EVENT_ROLE_CATALOG) as WwmEventRoleKey[]
                  ).map((key) => (
                    <label
                      key={key}
                      className="flex items-center gap-2 text-sm"
                    >
                      <Checkbox
                        checked={field.state.value.includes(key)}
                        onBlur={field.handleBlur}
                        onCheckedChange={(checked) =>
                          field.handleChange(
                            checked
                              ? [...field.state.value, key]
                              : field.state.value.filter(
                                  (role: WwmEventRoleKey) => role !== key,
                                ),
                          )
                        }
                      />
                      {getRoleLabel(key)}
                    </label>
                  ))}
                </div>
                <FieldError error={error} id={errorId} />
              </fieldset>
            )
          })
        : null}
      {bindings.rsvpEnabled((field) => (
        <label className="flex items-start gap-2 text-sm md:col-span-2">
          <Checkbox
            checked={field.state.value}
            onBlur={field.handleBlur}
            onCheckedChange={(checked) => field.handleChange(checked)}
          />
          <span className="grid gap-1">
            <span className="font-medium">Enable RSVPs</span>
            <span className="text-muted-foreground">
              Members can RSVP and view the roster.
            </span>
          </span>
        </label>
      ))}
      {bindings.values.eventKind === "attendance" ? (
        <p className="text-sm text-muted-foreground md:col-span-2">
          RSVPs stay open until the event starts.
        </p>
      ) : null}
      {bindings.startAt((field) => {
        const error = firstError(field.state.meta.errors)
        const errorId = "event-start-at-error"
        const hintId = "event-start-at-hint"
        return (
          <div className="grid gap-2">
            <label
              htmlFor="event-start-at"
              className="text-xs font-semibold tracking-wide text-muted-foreground uppercase"
            >
              Start at
            </label>
            <DateTimeField
              id="event-start-at"
              value={field.state.value}
              onBlur={field.handleBlur}
              onValueChange={(startAt) => field.handleChange(startAt ?? "")}
              ariaInvalid={error ? true : undefined}
              ariaDescribedBy={[hintId, error ? errorId : null]
                .filter(Boolean)
                .join(" ")}
              dateAriaLabel="Start date"
              timeAriaLabel="Start time"
              minuteStep={1}
            />
            {field.state.value ? (
              <p id={hintId} className="text-xs text-muted-foreground">
                {formatDateTimeOffsetHint(field.state.value)}
              </p>
            ) : null}
            <FieldError error={error} id={errorId} />
          </div>
        )
      })}
      {bindings.values.eventKind === "raid"
        ? bindings.signupCloseAt((field) => {
            const error = firstError(field.state.meta.errors)
            const errorId = "event-signup-close-at-error"
            const hintId = "event-signup-close-at-hint"
            return (
              <div className="grid gap-2">
                <label
                  htmlFor="event-signup-close-at"
                  className="text-xs font-semibold tracking-wide text-muted-foreground uppercase"
                >
                  Signup close at
                </label>
                <DateTimeField
                  id="event-signup-close-at"
                  value={field.state.value ?? ""}
                  onBlur={field.handleBlur}
                  onValueChange={(signupCloseAt) =>
                    field.handleChange(signupCloseAt ?? "")
                  }
                  ariaInvalid={error ? true : undefined}
                  ariaDescribedBy={[hintId, error ? errorId : null]
                    .filter(Boolean)
                    .join(" ")}
                  dateAriaLabel="Signup close date"
                  timeAriaLabel="Signup close time"
                  minuteStep={1}
                />
                {field.state.value ? (
                  <p id={hintId} className="text-xs text-muted-foreground">
                    {formatDateTimeOffsetHint(field.state.value)}
                  </p>
                ) : null}
                <FieldError error={error} id={errorId} />
              </div>
            )
          })
        : null}
    </>
  )
}

export function validateEventInput(
  value: EventFormState,
  {
    now = new Date(),
    requireFutureClose = false,
  }: { now?: Date; requireFutureClose?: boolean } = {},
) {
  const fields: EventFormErrors = {}
  if (!value.title.trim()) fields.title = "Title is required."

  const startDate = new Date(value.startAt)
  if (!value.startAt) fields.startAt = "Event start time is required."
  else if (isNaN(startDate.getTime())) fields.startAt = "Invalid date format."
  else if (startDate <= now)
    fields.startAt = "Event start time must be in the future."

  if (value.eventKind === "raid") {
    const closeDate = new Date(value.signupCloseAt)
    if (!value.signupCloseAt)
      fields.signupCloseAt = "Signup close time is required for raids."
    else if (isNaN(closeDate.getTime()))
      fields.signupCloseAt = "Invalid date format."
    else if (closeDate >= startDate)
      fields.signupCloseAt = "Signup close must be before the event start time."
    else if (requireFutureClose && closeDate < now)
      fields.signupCloseAt = "Signup close time must be in the future."
    if (value.eventRoles.length === 0)
      fields.eventRoles = "At least one role is required for raids."
  }

  return Object.keys(fields).length > 0 ? { fields } : undefined
}

export function projectEventInput(value: EventFormState): EventFormInput {
  const startAt = new Date(value.startAt).toISOString()
  if (value.eventKind === "attendance") {
    return {
      eventKind: "attendance",
      title: value.title.trim(),
      description: value.description.trim(),
      startAt,
      eventRoles: [],
      signupCloseAt: null,
      rsvpEnabled: value.rsvpEnabled,
    }
  }

  const [firstRole, ...otherRoles] = value.eventRoles
  if (!firstRole) throw new Error("Raid event requires at least one role.")
  return {
    eventKind: "raid",
    title: value.title.trim(),
    description: value.description.trim(),
    startAt,
    eventRoles: [firstRole, ...otherRoles],
    signupCloseAt: new Date(value.signupCloseAt).toISOString(),
    rsvpEnabled: value.rsvpEnabled,
  }
}
